import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles, Collapse, Fade } from '@material-ui/core';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import RichTextPreview from '../../richTextPreview';
import UserAvatar from '../../userAvatar';
import ActionButton from '../../actionButton';
import { ACTION_BUTTON_TYPES } from '../../actionButton/config';
import { getOrderedRankingOptions } from '../../../../utility/fillSurveyUtils';
import { isArrayEmpty } from '../../../../utility/helpers';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      marginTop: spacing(6),
    },
    question: {
      backgroundColor: primary.bluish9,
      borderRadius: 8,
      padding: spacing(6),
      marginBottom: spacing(4),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    info: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 30,
    },
    infoClickable: {
      cursor: 'pointer',
    },
    titleWrapper: {
      display: 'flex',
      alignItems: 'center',
      flexGrow: 1,
      maxWidth: '100%',
    },
    titleWrapperCollapsed: {
      maxWidth: 'calc(100% - 36px)',
    },
    collapseButton: {
      marginLeft: spacing(1.5),
      alignSelf: 'flex-start',
    },
    expandIcon: {
      transform: 'rotate(0deg)',
      transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
    },
    expandIconOpened: {
      transform: 'rotate(180deg)',
    },
    answers: {
      marginTop: spacing(6),
    },
    answerWrapper: {
      marginBottom: spacing(6),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    userAvatar: {
      marginBottom: spacing(3),
    },
    answerContent: {
      boxSizing: 'border-box',
      backgroundColor: primary.white,
      border: `1px solid ${primary.bluish6}`,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'flex-start',
      minWidth: 0,
      padding: spacing(4),
      marginBottom: spacing(2),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    answerContentEmpty: {
      backgroundColor: 'transparent',
    },
    answerText: {
      flexGrow: 1,
      minWidth: 0,
      fontSize: 14,
      lineHeight: '16px',
      alignSelf: 'center',
      wordWrap: 'break-word',
      [breakpoints.up('sm')]: {
        fontSize: 16,
        lineHeight: '18px',
      },
    },
    answerTextEmpty: {
      color: primary.bluish6,
      '& a': {
        color: primary.bluish6,
      },
    },
  })
);

const RankingResults = ({
  className = null,
  question,
  options = [],
  results = [],
  selectedQuestionId = null,
  onExpand,
}) => {
  const classes = useStyles();
  const hasResults = !isArrayEmpty(results);
  const isExpanded = selectedQuestionId === question.id;

  const renderOption = (option, isEmpty = false) => (
    <div
      key={`ranking_option_${option.id}`}
      className={classNames(classes.answerContent, {
        [classes.answerContentEmpty]: isEmpty,
      })}
    >
      <RichTextPreview
        text={option.text}
        className={classNames(classes.answerText, {
          [classes.answerTextEmpty]: isEmpty,
        })}
        variant="body1"
      />
    </div>
  );

  const renderEmptyState = () => (
    <div className={classes.answers}>
      {options.map(option => renderOption(option, true))}
    </div>
  );

  const renderResults = () => (
    <div className={classes.answers}>
      {results.map(({ user, rankedOptionIds }, index) => {
        const orderedOptions = getOrderedRankingOptions(
          options,
          rankedOptionIds
        );

        return (
          <div
            key={`ranking_user_${user.id}_${index}`}
            className={classes.answerWrapper}
          >
            <UserAvatar
              className={classes.userAvatar}
              user={user}
              variant="subtitle2"
              small
              caption
            />
            {orderedOptions.map(option => renderOption(option))}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.question}>
        <div
          className={classNames(classes.info, {
            [classes.infoClickable]: hasResults,
          })}
          onClick={hasResults ? onExpand(question.id) : undefined}
        >
          <div
            className={classNames(classes.titleWrapper, {
              [classes.titleWrapperCollapsed]: hasResults,
            })}
          >
            <TextBoxWithTooltip variant="h4" text={question.text} />
          </div>
          <Fade in={hasResults} unmountOnExit>
            <ActionButton
              className={classes.collapseButton}
              iconClass={classNames(classes.expandIcon, {
                [classes.expandIconOpened]: isExpanded,
              })}
              size="small"
              edge="end"
              type={ACTION_BUTTON_TYPES.ARROW}
              onClickHandler={onExpand(question.id)}
            />
          </Fade>
        </div>
        {hasResults ? (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {renderResults()}
          </Collapse>
        ) : (
          renderEmptyState()
        )}
      </div>
    </div>
  );
};

RankingResults.propTypes = {
  className: PropTypes.string,
  question: PropTypes.shape({}).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  results: PropTypes.arrayOf(PropTypes.shape({})),
  selectedQuestionId: PropTypes.number,
  onExpand: PropTypes.func.isRequired,
};

export default RankingResults;
